/**
 * KarnoffelTutorial — single-player Karnöffel against a local AI.
 *
 * Pure client-side: no WebSocket, no Mongo, no room key.  Engine
 * logic + AI live in `lib/karnoffelLocal.js` and mirror the backend
 * `karnoffel_engine.py`.  The visual layout intentionally mirrors
 * `KarnoffelRoom.jsx` so what the player learns here maps 1:1 to
 * the live multiplayer experience.
 *
 * Coaching layer: contextual one-line tips appear above the trick
 * area depending on phase (leading vs following, void in led suit,
 * trump in hand, etc.).  Visible "Trump Ladder" reference card on
 * the right.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import NavPane from "@/components/NavPane";
import { KARNOFFEL_CFG } from "@/components/MatchKey/cfgs";
import {
  dealRound, resolveTrick, canPlay, aiChoose, cardPower,
  SPECIAL_NAMES,
} from "@/lib/karnoffelLocal";

const PAL = KARNOFFEL_CFG.palette;
const FONTS = KARNOFFEL_CFG.fonts;
const TIMES = "'Times New Roman', Times, serif";
const SUIT_GLYPH = { S: "\u2660", H: "\u2665", D: "\u2666", C: "\u2663" };
const SUIT_COLOR = { S: "#0F0E0C", C: "#0F0E0C", H: "#B33333", D: "#B33333" };
const TRUMP_NAMES_BY_RANK = { "2": "Devil", "7": "Karn\u00f6ffel", J: "Pope", "6": "Kaiser" };

function Card({ c, faceDown, onClick, disabled, highlight }) {
  if (faceDown) {
    return (
      <div
        className="rounded-md"
        style={{
          width: 60, height: 86,
          background: "linear-gradient(135deg,#3A2A1B 0%,#5C3A1E 50%,#2C1B0F 100%)",
          border: "1px solid #C9A560",
          boxShadow: "0 2px 4px rgba(0,0,0,0.55)",
        }}
      />
    );
  }
  if (!c) return null;
  return (
    <button
      onClick={onClick}
      data-silent="true"
      data-testid={`karnoffel-tut-card-${c.id}`}
      disabled={disabled}
      style={{
        width: 60, height: 86,
        background: disabled ? "#E5DEC8" : "#FFFDF6",
        color: SUIT_COLOR[c.s],
        opacity: disabled ? 0.55 : 1,
        border: highlight ? "2px solid #E5C870" : "1px solid #C4B083",
        borderRadius: 6,
        boxShadow: highlight
          ? "0 0 0 2px #E5C870, 0 4px 8px rgba(0,0,0,0.45)"
          : "0 2px 4px rgba(0,0,0,0.5)",
        fontFamily: TIMES, fontSize: 18, fontWeight: 700,
        padding: 6, display: "flex", flexDirection: "column", alignItems: "flex-start",
        cursor: disabled ? "default" : "pointer",
        transform: highlight ? "translateY(-6px)" : "translateY(0)",
        transition: "transform 0.15s ease-out, box-shadow 0.15s ease-out",
      }}
    >
      <span>{c.r}</span>
      <span style={{ fontSize: 24, alignSelf: "center", marginTop: 2 }}>{SUIT_GLYPH[c.s]}</span>
    </button>
  );
}

function TrumpLadder({ trump }) {
  const rows = [
    { r: "2", name: "Devil", note: "beats everything" },
    { r: "7", name: "Karn\u00f6ffel", note: "beats all except Devil" },
    { r: "J", name: "Pope (Unter)", note: "beats all plain + lower trumps" },
    { r: "6", name: "Kaiser", note: "" },
    { r: "3", name: "", note: "" },
    { r: "5", name: "", note: "" },
    { r: "4", name: "", note: "" },
    { r: "K/Q/10/9/8", name: "low trumps", note: "still beat any plain card" },
  ];
  return (
    <div
      data-testid="karnoffel-tut-ladder"
      style={{
        background: "rgba(245,235,208,0.04)",
        border: `1px solid ${PAL.border}`,
        borderRadius: 4,
        padding: "12px 16px",
        fontFamily: FONTS.body,
        color: PAL.ink, fontSize: 16, maxWidth: 360,
      }}
    >
      <div style={{ fontFamily: FONTS.eyebrow, color: PAL.accent, letterSpacing: "0.1em", marginBottom: 6, fontSize: 16 }}>
        TRUMP LADDER {trump ? `\u2014 ${SUIT_GLYPH[trump]} ${trump}` : ""}
      </div>
      <table style={{ width: "100%", borderSpacing: 0, color: PAL.muted }}>
        <tbody>
          {rows.map((row) => (
            <tr key={row.r} style={{ verticalAlign: "top" }}>
              <td style={{ color: PAL.accent, fontWeight: 700, paddingRight: 8, whiteSpace: "nowrap" }}>{row.r}</td>
              <td style={{ color: PAL.ink, paddingRight: 8, whiteSpace: "nowrap" }}>{row.name}</td>
              <td style={{ color: PAL.muted, fontStyle: "italic", fontSize: 16 }}>{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MATCH_TARGET = 3;

export default function KarnoffelTutorial() {
  const navigate = useNavigate();

  // "you" = human (host), "ai" = bot (guest).  Engine uses host/guest
  // semantics — we keep the same naming so the JS port stays simple.
  const [deal, setDeal] = useState(() => dealRound());
  const [scores, setScores] = useState({ host: 0, guest: 0 });
  const [phase, setPhase] = useState("play"); // play | finished
  const [msg, setMsg] = useState(
    "Round dealt.  Trump is the suit of the turn-up card.  Guest (AI) leads.",
  );
  const [lastTrick, setLastTrick] = useState(null);
  const aiTimer = useRef(null);

  const yourHand = deal.hands.host;
  const oppHand = deal.hands.guest;
  const led = deal.led;
  const ledSuit = led ? led.card.s : null;

  // Whose turn?  If `led` is null, the leader plays; else the opposite.
  const turnRole = useMemo(() => {
    if (!led) return deal.leader;
    return deal.leader === "host" ? "guest" : "host";
  }, [deal, led]);
  const isYourTurn = phase === "play" && turnRole === "host";

  const canPlayCard = (c) => {
    if (!isYourTurn) return false;
    return canPlay(c, yourHand, ledSuit);
  };

  // ── Game-flow helpers ────────────────────────────────────────────
  const playCard = (role, card) => {
    setDeal((prev) => {
      const next = {
        ...prev,
        hands: { ...prev.hands, [role]: prev.hands[role].filter((c) => c.id !== card.id) },
        led: prev.led,
        tricks: { ...prev.tricks },
      };
      if (!prev.led) {
        next.led = { card, role };
        const label = labelCard(card, prev.trump);
        setMsg(`${role === "host" ? "You" : "AI"} led ${label}.`);
        return next;
      }
      const ledCard = prev.led.card;
      const winnerIdx = resolveTrick(ledCard, card, prev.trump);
      const winnerRole = winnerIdx === 0 ? prev.led.role : role;
      next.tricks[winnerRole] = (next.tricks[winnerRole] || 0) + 1;
      next.led = null;
      next.leader = winnerRole;
      setLastTrick({
        led: prev.led,
        follow: { card, role },
        winner: winnerRole,
      });
      setMsg(
        `${prev.led.role === "host" ? "You" : "AI"} led ${labelCard(ledCard, prev.trump)}; ` +
          `${role === "host" ? "you" : "AI"} played ${labelCard(card, prev.trump)}.  ` +
          `${winnerRole === "host" ? "You take" : "AI takes"} the trick.`,
      );
      return next;
    });
  };

  // Round-end / match-end resolution.  Runs after every state update.
  useEffect(() => {
    if (phase !== "play") return;
    if (deal.hands.host.length === 0 && deal.hands.guest.length === 0) {
      const h = deal.tricks.host, g = deal.tricks.guest;
      let nextScores = { ...scores };
      let blurb = "";
      if (h > g) {
        const pts = g === 0 ? 2 : 1;
        nextScores.host += pts;
        blurb = `Round over.  You win ${h}-${g}${pts === 2 ? " — SWEEP! +2." : " — +1."}`;
      } else if (g > h) {
        const pts = h === 0 ? 2 : 1;
        nextScores.guest += pts;
        blurb = `Round over.  AI wins ${g}-${h}${pts === 2 ? " — SWEEP! +2." : " — +1."}`;
      } else {
        blurb = "Round tied.  No points scored.";
      }
      setScores(nextScores);
      if (nextScores.host >= MATCH_TARGET || nextScores.guest >= MATCH_TARGET) {
        const winner = nextScores.host >= MATCH_TARGET ? "You" : "AI";
        setMsg(`${blurb}  ${winner} won the match!`);
        setPhase("finished");
      } else {
        setMsg(blurb + "  Dealing next round\u2026");
        const id = setTimeout(() => {
          setDeal(dealRound());
          setLastTrick(null);
          setMsg("New round dealt.  Guest (AI) leads.");
        }, 2200);
        return () => clearTimeout(id);
      }
    }
  }, [deal, phase, scores]);

  // AI takes its turn whenever it is the AI's turn to play.
  useEffect(() => {
    if (phase !== "play") return;
    if (turnRole !== "guest") return;
    if (deal.hands.guest.length === 0) return;
    if (aiTimer.current) clearTimeout(aiTimer.current);
    aiTimer.current = setTimeout(() => {
      const choice = aiChoose(deal.hands.guest, deal, "guest");
      if (choice) playCard("guest", choice);
    }, 900);
    return () => { if (aiTimer.current) clearTimeout(aiTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, phase, turnRole]);

  // Coaching tip line.
  const tip = useMemo(() => {
    if (phase === "finished") return "Match complete \u2014 click 'New Match' to play again.";
    if (turnRole === "guest") return "AI is thinking\u2026";
    if (!led) {
      const trumps = yourHand.filter((c) => c.s === deal.trump);
      if (trumps.length >= 3) return "Tip: you have several trumps \u2014 leading a low non-trump can lure the AI into wasting one.";
      return "Tip: you lead this trick. Saving trumps for later usually pays.";
    }
    const hasSuit = yourHand.some((c) => c.s === ledSuit);
    if (!hasSuit) return "Tip: you're void in the led suit \u2014 play any card. A trump will win the trick; a non-trump cannot.";
    return "Tip: you have the led suit \u2014 you must follow it.";
  }, [phase, turnRole, led, yourHand, deal.trump, ledSuit]);

  // AI-generated tip (LLM) — refreshed whenever it becomes the human's
  // turn during play.  Falls back to the static `tip` above while the
  // request is in flight or if the network fails.
  const [aiTip, setAiTip] = useState("");
  const [aiRestriction, setAiRestriction] = useState("");
  const [aiTipLoading, setAiTipLoading] = useState(false);
  const aiTipReqId = useRef(0);
  useEffect(() => {
    if (phase !== "play") return;
    if (turnRole !== "host") return;
    if (yourHand.length === 0) return;
    const reqId = ++aiTipReqId.current;
    setAiTip("");
    setAiRestriction("");
    setAiTipLoading(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    const body = {
      trump: deal.trump,
      turnup: deal.turnup,
      your_hand: yourHand,
      opp_hand_size: oppHand.length,
      led: led || null,
      tricks_you: deal.tricks.host || 0,
      tricks_ai: deal.tricks.guest || 0,
    };
    fetch(`${API}/api/karnoffel/tutorial/tip`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        if (reqId !== aiTipReqId.current) return;
        setAiTip(String(data.tip || "").trim());
        setAiRestriction(String(data.restriction || "").trim());
      })
      .catch(() => {
        if (reqId !== aiTipReqId.current) return;
        setAiTip("");
        setAiRestriction("");
      })
      .finally(() => {
        if (reqId === aiTipReqId.current) setAiTipLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, phase, turnRole]);

  const newMatch = () => {
    setDeal(dealRound());
    setScores({ host: 0, guest: 0 });
    setPhase("play");
    setLastTrick(null);
    setMsg("New match dealt.  Trump is the suit of the turn-up card.  Guest (AI) leads.");
  };

  return (
    <div className="min-h-screen karnoffel-scope" style={{ background: PAL.bg, color: PAL.ink }} data-testid="karnoffel-tutorial">
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(201,165,96,0.22)" }}
        >
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
            <h1
              data-testid="karnoffel-tutorial-title"
              style={{
                fontFamily: FONTS.title, fontWeight: FONTS.titleWeight,
                color: PAL.accent, fontSize: "clamp(2.2rem,4vw,3.6rem)",
                letterSpacing: FONTS.titleSpacing, textShadow: PAL.titleShadow,
              }}
            >
              Karn&ouml;ffel &mdash; Tutorial
            </h1>
            <div style={{ fontFamily: FONTS.body, color: PAL.muted, fontSize: 16 }}>
              Match{" "}
              <span style={{ color: PAL.ink }}>
                You {scores.host} &middot; AI {scores.guest}
              </span>{" "}
              (first to {MATCH_TARGET})
              <button
                data-silent="true"
                onClick={() => navigate("/games/karnoffel")}
                data-testid="karnoffel-tutorial-back-button"
                style={{
                  marginLeft: 16, fontFamily: FONTS.eyebrow, color: PAL.accent,
                  letterSpacing: "0.12em", fontSize: 16,
                  padding: "0.35rem 0.8rem", border: `1px solid ${PAL.border}`,
                  background: "transparent", cursor: "pointer", borderRadius: 2,
                }}
              >
                BACK TO LOBBY
              </button>
            </div>
          </div>

          <div className="mb-5" style={{ fontFamily: FONTS.body, color: PAL.accent }} data-testid="karnoffel-tutorial-status">
            {msg}
          </div>

          {/* Opponent (AI) hand */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="karnoffel-tutorial-ai-hand">
            {Array.from({ length: oppHand.length }).map((_, i) => (
              <Card key={i} faceDown />
            ))}
          </div>

          {/* Trick area */}
          <div className="flex flex-wrap items-end gap-6 mb-6">
            <div className="flex flex-col items-start">
              <div style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16, marginBottom: 4 }}>TRUMP</div>
              <Card c={deal.turnup} />
            </div>
            <div className="flex flex-col items-start">
              <div style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16, marginBottom: 4 }}>LED</div>
              {led ? <Card c={led.card} highlight={led.card.s === deal.trump} /> : <Card faceDown />}
            </div>
            <div className="flex flex-col items-start">
              <div style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16, marginBottom: 4 }}>YOU</div>
              {lastTrick && lastTrick.follow.role === "host" ? (
                <Card c={lastTrick.follow.card} />
              ) : lastTrick && lastTrick.led.role === "host" && !led ? (
                <Card c={lastTrick.led.card} />
              ) : (
                <Card faceDown />
              )}
            </div>
            <div className="flex flex-col items-start" style={{ marginLeft: "auto" }}>
              <div style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16, marginBottom: 4 }}>
                TRICKS &mdash; You {deal.tricks.host} &middot; AI {deal.tricks.guest}
              </div>
              {lastTrick && (
                <div style={{ fontFamily: FONTS.body, color: PAL.muted, fontSize: 16, fontStyle: "italic" }}>
                  Last: {lastTrick.winner === "host" ? "you won" : "AI won"}
                </div>
              )}
            </div>
          </div>

          {/* Your hand */}
          <div className="mb-5" style={{ fontFamily: FONTS.eyebrow, color: PAL.muted, letterSpacing: "0.1em", fontSize: 16 }}>YOUR HAND</div>
          <div className="flex flex-wrap gap-3 mb-8" data-testid="karnoffel-tutorial-my-hand">
            {yourHand.map((c) => {
              const isTrump = c.s === deal.trump;
              const tname = isTrump ? TRUMP_NAMES_BY_RANK[c.r] : null;
              return (
                <div key={c.id} className="flex flex-col items-center" style={{ minWidth: 60 }}>
                  <Card
                    c={c}
                    disabled={!canPlayCard(c)}
                    onClick={() => canPlayCard(c) && playCard("host", c)}
                  />
                  {tname && (
                    <span style={{ marginTop: 2, fontFamily: FONTS.body, color: PAL.accent, fontSize: 16, fontStyle: "italic" }}>
                      {tname}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {phase === "finished" && (
            <div className="mb-6">
              <button
                data-testid="karnoffel-tutorial-new-match"
                onClick={newMatch}
                style={{
                  fontFamily: FONTS.eyebrow, letterSpacing: "0.12em",
                  padding: "0.6rem 1.4rem", background: PAL.accent,
                  color: PAL.btnInk, border: `1px solid ${PAL.border}`, cursor: "pointer",
                }}
              >
                NEW MATCH
              </button>
            </div>
          )}

          {/* AI strategy tip — refreshed every human turn via LLM.
              Lives between the player's hand and the Trump Ladder per
              user iter M645. */}
          <div
            data-testid="karnoffel-tutorial-ai-tip"
            className="mb-5"
            style={{
              background: "rgba(245,235,208,0.04)",
              border: `1px solid ${PAL.border}`,
              borderRadius: 4,
              padding: "10px 14px",
              maxWidth: 720,
              fontFamily: FONTS.body,
              color: PAL.ink,
              fontSize: 16,
              lineHeight: 1.45,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.eyebrow,
                color: PAL.accent,
                letterSpacing: "0.1em",
                fontSize: 16,
                marginBottom: 4,
              }}
            >
              AI COACH
            </div>
            {aiTipLoading && !aiTip && !aiRestriction ? (
              <div style={{ fontStyle: "italic", color: PAL.muted }}>
                Thinking&hellip;
              </div>
            ) : (
              <ul
                style={{ listStyle: "disc", paddingLeft: "1.2rem", margin: 0 }}
                data-testid="karnoffel-tutorial-ai-tip-list"
              >
                <li
                  data-testid="karnoffel-tutorial-ai-tip-bullet"
                  style={{ marginBottom: aiRestriction ? 6 : 0, color: PAL.ink }}
                >
                  {aiTip || tip}
                </li>
                {aiRestriction && (
                  <li
                    data-testid="karnoffel-tutorial-ai-restriction-bullet"
                    style={{ color: PAL.ink }}
                  >
                    {aiRestriction}
                  </li>
                )}
              </ul>
            )}
          </div>

          <TrumpLadder trump={deal.trump} />

          {/* Glossary tips — per user iter M647.  Sits underneath the
              Trump Ladder.  Starts with two bullets defining the
              terminology that confuses new players the most. */}
          <div
            data-testid="karnoffel-tutorial-glossary"
            className="mt-5"
            style={{
              background: "rgba(245,235,208,0.04)",
              border: `1px solid ${PAL.border}`,
              borderRadius: 4,
              padding: "12px 16px",
              maxWidth: 720,
              fontFamily: FONTS.body,
              color: PAL.ink,
              fontSize: 16,
              lineHeight: 1.5,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.eyebrow,
                color: PAL.accent,
                letterSpacing: "0.1em",
                fontSize: 16,
                marginBottom: 8,
              }}
            >
              TIPS
            </div>
            <ul style={{ listStyle: "disc", paddingLeft: "1.2rem", margin: 0 }}>
              <li
                data-testid="karnoffel-tutorial-glossary-trick"
                style={{ marginBottom: 8 }}
              >
                <strong style={{ color: PAL.accent }}>Trick</strong> &mdash;
                one round of two cards: the leader plays one card,
                then the follower plays one card on top of it.  The
                higher of the two (per the Trump Ladder and led-suit
                rules) wins the trick.  A round of Karn&ouml;ffel is
                a series of tricks; whoever takes more tricks wins
                the round.
              </li>
              <li
                data-testid="karnoffel-tutorial-glossary-lead"
                style={{ marginBottom: 8 }}
              >
                <strong style={{ color: PAL.accent }}>To lead</strong>{" "}
                &mdash; to be the first to play a card in a trick.
                The suit of that first card is the &ldquo;led
                suit&rdquo;; the follower must play a card of that
                same suit if they have one.  After a trick is
                resolved, the player who took the trick leads the
                next one.
              </li>
              <li
                data-testid="karnoffel-tutorial-glossary-hold"
                style={{ marginBottom: 8 }}
              >
                <strong style={{ color: PAL.accent }}>To &ldquo;hold a card of the led suit&rdquo;</strong>{" "}
                &mdash; simply to have at least one card in your hand
                whose suit (&spades;, &hearts;, &diams;, or
                &clubs;) matches the suit of the card the AI just led.
                For example, if the AI led the 5&diams; and your hand
                contains any diamond at all, you hold the led suit
                and must follow it; if your hand has only spades,
                hearts, and clubs, you do not hold the led suit
                (&ldquo;void&rdquo;) and may play any card.
              </li>
              <li
                data-testid="karnoffel-tutorial-glossary-follow"
                style={{ marginBottom: 0 }}
              >
                <strong style={{ color: PAL.accent }}>Why you must follow the led suit</strong>{" "}
                &mdash; if you hold a card of the led suit, you must
                play one, since the rule keeps the trick an honest
                contest in a single suit rather than a free-for-all.
                The one extenuating circumstance is being <em>void</em>{" "}
                in the led suit, in which case you may play any
                card &mdash; a trump (which wins) or an off-suit
                non-trump (which cannot win but lets you discard).
              </li>
            </ul>
          </div>
          {/* Unused: SPECIAL_NAMES is imported but referenced via TRUMP_NAMES_BY_RANK above. */}
          <span style={{ display: "none" }}>{Object.keys(SPECIAL_NAMES).join(",")}</span>
          {/* Reference cardPower so eslint doesn't complain about the unused import that we need for tests. */}
          <span style={{ display: "none" }}>{cardPower(deal.turnup, deal.trump, null)}</span>
        </main>
      </div>
    </div>
  );
}

function labelCard(card, trumpSuit) {
  const suit = SUIT_GLYPH[card.s] || "?";
  const name = card.s === trumpSuit ? (TRUMP_NAMES_BY_RANK[card.r] || "") : "";
  return name ? `${card.r}${suit} (${name})` : `${card.r}${suit}`;
}
